import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import MessageCard from '../Home/MessageCard';
import { UserAuth } from '@/app/context/AuthContext';
import { FREE_HISTORY_LABEL } from '@/utils/paywall';

const MessagesSlider = ({ messages, title, fetchMoreMessages, paywalled = false, blurredMessages = [], onUpgrade }) => {
	const [currentPageMessages, setCurrentPageMessages] = useState([]);
	const [loading, setLoading] = useState(false);
	const containerRef = useRef(null);
	const router = useRouter();
	const { user, isPro, userLoading, authReady, googleSignIn } = UserAuth();
	// Never flash the upsell at a pro member while their account is still loading.
	const showPaywall = paywalled && authReady && !(user && (isPro || userLoading));

	useEffect(() => {
		setCurrentPageMessages(messages);
	}, [messages]);

	const handleScroll = async () => {
		const container = containerRef.current;

		if (
			container &&
			container.scrollWidth - container.scrollLeft <=
				container.clientWidth + 50 &&
			!loading &&
			fetchMoreMessages
		) {
			setLoading(true);

			const newMessages = await fetchMoreMessages();
			if (newMessages && newMessages.length > 0) {
				setCurrentPageMessages((prev) => [...prev, ...newMessages]);
			}

			setLoading(false);
		}
	};

	const handlePaywallCta = async () => {
		if (onUpgrade) {
			onUpgrade();
			return;
		}
		if (user) {
			router.push('/pro');
		} else {
			try {
				await googleSignIn();
			} catch (err) {
				console.error('Sign-in failed:', err);
			}
		}
	};

	const blurredCard =
		paywalled && blurredMessages.length > 0 ? blurredMessages[0] : null;

	return (
		<div className='my-4 w-full min-w-0'>
			{title && (
				<div className='flex justify-center md:justify-between items-center mb-4 mt-8 px-3'>
					<h2 className='text-2xl font-medium'>{title}</h2>
				</div>
			)}
			<div
				className='flex overflow-x-auto gap-4 justify-start items-start w-full'
				ref={containerRef}
				onScroll={handleScroll}
			>
				{currentPageMessages?.map((message, i) => (
					<MessageCard
						key={message?.messageId || i}
						message={message}
						variant='slider'
					/>
				))}

				{/* Paywall modal inline at end of scroll — the blurred card behind
				    it renders at full height so the block matches the real message
				    cards beside it */}
				{showPaywall && (
					<div className='relative flex shrink-0'>
						{blurredCard ? (
							<div className='flex blur-[2px] opacity-60 pointer-events-none select-none'>
								<MessageCard
									key={`blurred-${blurredCard.messageId}`}
									message={blurredCard}
									variant='slider'
								/>
							</div>
						) : (
							<div className='w-[268px] min-h-[550px]' />
						)}
						<div className='absolute inset-0 flex items-center justify-center md:px-3'>
							{/* Content-sized panel centered over the full-height blurred
							    card (like desktop). Mobile: full card width + larger type.
							    Desktop (md+): the original compact w-60 box. */}
							<div className='rounded-2xl bg-white shadow-lg text-center w-full p-6 md:w-60 md:p-4'>
								<h2 className='text-xl md:text-lg font-bold text-black mb-2'>
									{user ? 'Unlock Full History' : 'Sign in to see more messages'}
								</h2>
								<p className='text-gray-500 text-base md:text-sm mb-5 md:mb-4'>
									{user
										? 'Get Pro to see all messages.'
										: `Create a free account to unlock ${FREE_HISTORY_LABEL} of history.`}
								</p>
								<button
									type='button'
									onClick={handlePaywallCta}
									className='rounded-lg bg-[#b232e5] font-semibold text-white hover:bg-[#9a28c7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#b232e5] px-8 py-3 text-base md:px-6 md:py-2 md:text-sm'
								>
									{user ? 'Get Pro' : 'Sign in with Google'}
								</button>
							</div>
						</div>
					</div>
				)}

				{loading && (
					<div className='flex items-center justify-center px-4 shrink-0'>
						<div className='w-8 h-8 border-4 border-t-purple-500 border-gray-200 rounded-full animate-spin'></div>
					</div>
				)}
			</div>
		</div>
	);
};

export default MessagesSlider;
